import { CLIRNET_API } from "./endpoints.js";
import { sessionStorage, STORAGE_KEYS } from "../utils/sessionStorage.js";
import { decodeJwtPayload } from "../utils/jwt.js";

// Refresh this long before the token's exp - for a token /refresh issued to this tab, whose
// remaining life is known without trusting the device clock.
const REFRESH_BUFFER_SECONDS = 3 * 60;
// ...and this long before it for a token of unknown age (the link's), which only the
// device clock can time. Refreshing that one early is what makes a slow device clock
// harmless: the first refresh still lands in time unless the clock is off by more than
// this, and every token after it is timed by its own life.
const UNKNOWN_AGE_REFRESH_BUFFER_SECONDS = 30 * 60;
const REFRESH_RETRY_MS = 30_000;
// Fallback cadence for tokens whose expiry can't be read (opaque token, or a
// JWT with no `exp` claim) — refresh on a fixed interval instead of never
// scheduling anything, so these sessions still roll their credential forward
// on their own instead of only ever refreshing reactively on a 401.
const FALLBACK_REFRESH_INTERVAL_SECONDS = 20 * 60;

function getStoredToken() {
  return sessionStorage.get(STORAGE_KEYS.AUTH_TOKEN);
}

// Tiny external store (React useSyncExternalStore-shaped): notified when the token is
// refused and whenever the stored token changes. AdminAuthGate shows "Your link has
// expired" while the token is refused, instead of the page failing call by call ("We
// couldn't load your details").
const authStatusListeners = new Set();
function notifyAuthStatusChange() {
  authStatusListeners.forEach((cb) => cb());
}
export function subscribeAuthStatus(callback) {
  authStatusListeners.add(callback);
  return () => authStatusListeners.delete(callback);
}
// The refusal reason, or null. It always belongs to the stored token: every change of
// token clears it (a refresh, a fresh link), so while it is set the
// stored token is the refused one - and a refused token never works again (the API only
// refreshes a still-valid token, and retires one as soon as it is refreshed).
export function getAuthStatusSnapshot() {
  return sessionStorage.get(STORAGE_KEYS.AUTH_LOGOUT_REASON);
}

// A fresh admin link starts clean (AuthGate).
export function clearAuthStatus() {
  if (!sessionStorage.get(STORAGE_KEYS.AUTH_LOGOUT_REASON)) return;
  sessionStorage.remove(STORAGE_KEYS.AUTH_LOGOUT_REASON);
  notifyAuthStatusChange();
}

function logout(reason) {
  console.warn("[auth] refresh rejected:", reason);
  sessionStorage.set(STORAGE_KEYS.AUTH_LOGOUT_REASON, reason || "unauthorized");
  notifyAuthStatusChange();
}

function storeToken(token, issued) {
  sessionStorage.set(STORAGE_KEYS.AUTH_TOKEN, token);
  if (issued) sessionStorage.set(STORAGE_KEYS.AUTH_TOKEN_ISSUED, issued);
  else sessionStorage.remove(STORAGE_KEYS.AUTH_TOKEN_ISSUED);
  sessionStorage.remove(STORAGE_KEYS.AUTH_LOGOUT_REASON);
  notifyAuthStatusChange();
}

function storeRefreshedToken(token) {
  const issued = issuedRecord(token);
  storeToken(token, issued);
  scheduleNextRefresh(token);
}

// A token /refresh just issued is valid from its nbf to its exp, both in server time: its
// whole life is known without any clock. Remember that life and when it arrived, so how
// long it has left is "life - time since it arrived" - measured by the wall clock, which
// keeps running while a laptop sleeps or a tab is frozen (a setTimeout countdown does
// not), and whose offset from the server's clock cancels out. (Not iat: the API carries
// the login's iat over to every refreshed token.) Kept in sessionStorage so a reload
// still knows it.
function issuedRecord(token) {
  const payload = decodeJwtPayload(token);
  return payload && typeof payload.exp === "number" && typeof payload.nbf === "number"
    ? { exp: payload.exp, nbf: payload.nbf, receivedAt: Date.now() }
    : null;
}

function issuedRecordFor(token, payload = decodeJwtPayload(token)) {
  const issued = sessionStorage.get(STORAGE_KEYS.AUTH_TOKEN_ISSUED);
  return payload && issued && issued.exp === payload.exp && issued.nbf === payload.nbf ? issued : null;
}

// How long `token` has before it expires, in ms - or null when its expiry can't be read.
// A token remembered above: life minus the time since it arrived (no device-clock offset).
// Any other token (the link's, of unknown age): its exp against the device clock - the
// only "now" the browser has.
export function msUntilExpiry(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return null;
  const issued = issuedRecordFor(token, payload);
  if (issued) return (issued.exp - issued.nbf) * 1000 - (Date.now() - issued.receivedAt);
  return payload.exp * 1000 - Date.now();
}

function refreshBufferMs(token) {
  return (issuedRecordFor(token) ? REFRESH_BUFFER_SECONDS : UNKNOWN_AGE_REFRESH_BUFFER_SECONDS) * 1000;
}

// ---------------------------------------------------------------------------------------
// Each tab keeps its own token data, in its own sessionStorage: nothing is sent to, asked
// of, or taken from other tabs. A tab holding the same token as another - a duplicated tab
// (its sessionStorage is copied), or the same link opened twice - is therefore refused at
// its next refresh once the other has refreshed it (the API retires a token the moment it
// is refreshed), and shows "Your link has expired".

// The login a token belongs to (same sub and iat: the API keeps the login's iat in every
// refreshed token), and its expiry.
function loginOf(token) {
  const p = decodeJwtPayload(token);
  return p && p.sub != null && typeof p.iat === "number" && typeof p.exp === "number"
    ? { sub: String(p.sub), iat: p.iat, exp: p.exp }
    : null;
}

// AuthGate: this tab already holds a later token of the link's own login (the same link
// opened again here) - keep it; the link's token was retired when it was refreshed.
export function isLaterTokenOfSameLogin(candidate, token) {
  const c = loginOf(candidate);
  const login = loginOf(token);
  return !!c && !!login && c.sub === login.sub && c.iat === login.iat && c.exp > login.exp;
}

// ---------------------------------------------------------------------------------------
let refreshTimer = null;
// When refreshTimer should fire, by the wall clock - see refreshIfDue.
let refreshTimerDueAt = 0;
// When the last refresh succeeded: a backend handing back short-lived tokens must not
// put the background loop into a tight refresh cycle.
let lastRefreshAt = 0;
const MIN_REFRESH_GAP_MS = 60_000;

function setRefreshTimer(fn, delay) {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(fn, delay);
  refreshTimerDueAt = Date.now() + delay;
}

function scheduleNextRefresh(token) {
  clearTimeout(refreshTimer);
  refreshTimer = null;
  const left = msUntilExpiry(token);

  const attempt = () => {
    refreshTimer = null;
    refreshToken().catch((e) => {
      // 401: the API refused this token (expired, or already swapped for a newer one) and
      // no retry can revive it - the app shows "link expired" instead (see logout). Only
      // a passing failure (offline, 5xx, timeout) is worth another try.
      if (e?.status === 401) return;
      console.warn("[auth] background refresh failed, retrying:", e.message);
      setRefreshTimer(attempt, REFRESH_RETRY_MS);
    });
  };

  if (left === null) {
    // Can't read an expiry — refresh on the fixed fallback cadence instead
    // of leaving this credential to expire with no proactive attempt at all.
    setRefreshTimer(attempt, FALLBACK_REFRESH_INTERVAL_SECONDS * 1000);
    return;
  }

  const delay = left - refreshBufferMs(token);
  if (delay > 0) {
    setRefreshTimer(attempt, delay);
    return;
  }

  // Already inside the buffer (page opened or reloaded late in the token's life, tab
  // resumed after sleep). /refresh only accepts a token that is still valid, so refresh
  // now: leaving it to the next request let the token expire first, and the refresh
  // then failed with 401 - the moderator was locked out with nothing called in time.
  const sinceLast = Date.now() - lastRefreshAt;
  setRefreshTimer(attempt, Math.max(MIN_REFRESH_GAP_MS - sinceLast, 1_000));
}

// Timers stop while a laptop sleeps or a background tab is frozen (mobile, bfcache), so the
// refresh timer can come due long after its time - the token expired meanwhile, and
// /refresh refuses an expired token. Re-check against the wall clock whenever the page
// comes back and every half minute; refresh at once if it is due.
const DUE_CHECK_INTERVAL_MS = 30_000;
let dueCheck = null;

function refreshIfDue() {
  const token = getStoredToken();
  // Nothing to refresh, already refreshing, or already refused (no retry revives that).
  if (!token || refreshPromise || getAuthStatusSnapshot()) return;
  // A timer that is not yet past due by the wall clock will fire on its own (a retry, say):
  // only one that slept through its time needs replacing.
  if (refreshTimer && refreshTimerDueAt >= Date.now()) return;
  const left = msUntilExpiry(token);
  if (left !== null && left <= refreshBufferMs(token)) scheduleNextRefresh(token);
}

function startDueChecks() {
  if (dueCheck || typeof window === "undefined") return;
  const onReturn = () => {
    if (document.visibilityState !== "hidden") refreshIfDue();
  };
  document.addEventListener("visibilitychange", onReturn);
  window.addEventListener("online", onReturn);
  dueCheck = { onReturn, interval: setInterval(refreshIfDue, DUE_CHECK_INTERVAL_MS) };
}

export function ensureTokenRefreshLoop() {
  const token = getStoredToken();
  if (!token) return;
  startDueChecks();
  if (refreshTimer || refreshPromise) return;
  scheduleNextRefresh(token);
}

// The refresh in flight, if any: axios.js holds requests until it settles, so they go out
// with the new token instead of the one it is retiring.
export function pendingRefresh() {
  return refreshPromise;
}

export function __resetAuthRefreshStateForTests() {
  clearTimeout(refreshTimer);
  refreshTimer = null;
  refreshPromise = null;
  lastRefreshAt = 0;
  if (dueCheck) {
    clearInterval(dueCheck.interval);
    document.removeEventListener("visibilitychange", dueCheck.onReturn);
    window.removeEventListener("online", dueCheck.onReturn);
    dueCheck = null;
  }
}

function refusedError(message) {
  return Object.assign(new Error(message), { status: 401 });
}

let refreshPromise = null;
export function refreshToken() {
  const oldToken = getStoredToken();
  if (!oldToken) return Promise.reject(new Error("No refresh credential"));
  if (refreshPromise) return refreshPromise;
  // Already refused: sending the same token again can only earn another 401.
  if (getAuthStatusSnapshot()) return Promise.reject(refusedError("Token already refused"));
  // refreshPromise is set before anything is awaited: with the import awaited first, two
  // callers in the same tick (say the user-details and Ably requests on load) both got
  // past the check above and sent two refreshes with the same token. The API retires a
  // token once it is refreshed, so the second one came back 401 - read as a dead token,
  // a false "Your link has expired".
  refreshPromise = import("./axios.js")
    .then(({ apiClient }) =>
      apiClient.get(CLIRNET_API.AUTH_REFRESH, {
        skipAuthRefresh: true,
        headers: { Authorization: `Bearer ${oldToken}` },
      }),
    )
    .then((res) => {
      const data = res.data || {};
      const fresh = data.access_token || data.token || data.data?.access_token || data.data?.token;
      if (!fresh) throw new Error("Refresh response had no token");
      lastRefreshAt = Date.now();
      storeRefreshedToken(fresh);
      return fresh;
    })
    .catch((err) => {
      // Only a 401 means a dead token — a transient failure (offline, 5xx, 429, timeout)
      // leaves the token as it is for the caller's own retry.
      if (err?.status === 401) logout(err.message);
      throw err;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

export { getStoredToken };
export const REFRESH_BUFFER = REFRESH_BUFFER_SECONDS;
